import { action, computed, observable, makeObservable } from 'mobx'
import InfiniteScrollProvider from '../../../utils/InfiniteScrollProvider'
import Document from '../../Document'
import ModalViewModel from '../../../infra/ModalViewModel'
import { task } from 'mobx-task'
import { createTransformer } from 'mobx-utils'

/**
 * Ports for the Document Picker.
 */
export interface DocumentPickerPorts {
  /**
   * Scroll provider used for the Link Document feature.
   */
  scrollProvider(): InfiniteScrollProvider<Document>

  /**
   * Delegate used to filter out documents that are not applicable for linking.
   * Can be reactive, as it is used in a computed.
   *
   * @param doc
   */
  canLinkDocument(doc: Document): boolean

  /**
   * Reactive getter for the documents. Should have the same filtering and sorting applied
   * as the scroll provider being used. This is what makes sure that whenever new docs come in
   * through events or are uploaded on the client that they show up without having to clear the scroll
   * provider and re-fetch.
   */
  documents(): Document[]

  /**
   * Whether the specified document is already selected. The selection
   * is tracked outside the viewmodel.
   *
   * @param doc
   */
  isSelected(doc: Document): boolean

  /**
   * Called when a document is being (de)selected.
   */
  onSelect(doc: Document): Promise<unknown>
}

/**
 * Document picker view model.
 */
export class DocumentPickerViewModel extends ModalViewModel {
  @observable searchText = ''

  /**
   * Transforms the document to a view model.
   *
   * @private
   */
  private toViewModel = createTransformer<
    Document,
    DocumentPickerViewModelItem
  >(
    (doc) =>
      new DocumentPickerViewModelItem(
        doc,
        () => this.isSelected(doc),
        () => this.select(doc),
      ),
  )

  constructor(private adapter: DocumentPickerPorts) {
    super()
    makeObservable(this)
  }

  show() {
    this.searchText = ''
    super.show()
  }

  @computed
  get scrollProvider() {
    return this.adapter.scrollProvider()
  }

  @computed
  get documents() {
    return this.adapter
      .documents()
      .filter((d) => this.canLinkDocument(d) && this.matchesSearchText(d))
      .map(this.toViewModel)
  }

  canLinkDocument(doc: Document) {
    return this.adapter.canLinkDocument(doc)
  }

  isSelected(doc: Document) {
    return this.adapter.isSelected(doc)
  }

  @action.bound
  setSearchText(value: string) {
    this.searchText = value
  }

  async select(doc: Document) {
    await this.adapter.onSelect(doc)
  }

  private matchesSearchText(d: Document) {
    const lowered = this.searchText.toLowerCase().trim()
    if (lowered.length === 0) return true
    return Boolean(
      d.title.toLowerCase().includes(lowered) ||
        d.description.toLowerCase().includes(lowered),
    )
  }
}

/**
 * Item view model for the picker.
 */
export class DocumentPickerViewModelItem {
  constructor(
    public document: Document,
    private _isSelected: () => boolean,
    private _onSelect: () => Promise<unknown>,
  ) {
    makeObservable(this)
  }

  @computed
  get fileName() {
    return this.document?.title || ''
  }

  @computed
  get isSelected() {
    return this._isSelected()
  }

  select = task.resolved(async () => {
    await this._onSelect()
  })
}
